import theme, { Brand } from '../../../lib/theme';
import React, { useEffect, useState } from 'react';
import Button from '../../../components/presentational/Button';
import Icon from '../../../components/presentational/Icon';
import Spacer from '../../../components/presentational/Spacer';
import { formatHolidayCostAsInteger } from '../../../lib/currencyUtilities';
import styled, { StylesProps, cx } from '../../../lib/styles';
import { Holiday } from '../../../models/holiday';
import BlockWrapper from '../BlockWrapper';
import { ButtonPosition, DefaultBlockFields } from '../GroupBlock';

import holidayBlockStyles from './HolidayBlock_styles';
import { CallToActionButton } from '../CallToActionButton';
import HolidayCard from './HolidayCard';
import globalStyles from '../../../lib/styles/Global_styles';
import { compose } from 'recompose';
import Typography from '../../../components/presentational/Typography';
import BlockTitle from '../BlockTitle';
import { getHolidaySlug } from '../../../lib/holidayUtils';
import _ from 'lodash';

const { layouts, scale, media, colors, font } = theme;

export interface HolidayBlockProps {
  type: 'acf_holiday_block';
  brand: string;
  classes: string;
  background_colour: string;
  desktop_spacing: string;
  mobile_spacing: string;
  featured_holiday: boolean;
  block: HolidayBlockFields;
}

export interface HolidayBlockFields extends DefaultBlockFields {
  columns_per_row: number;
  holidays: Holiday[];
}

const HolidayBlock = ({
  type,
  classes,
  brand,
  background_colour,
  desktop_spacing,
  mobile_spacing,
  featured_holiday,
  block: { title, subtitle, columns_per_row, holidays, cta_button },
  styles,
}: HolidayBlockProps & StylesProps<typeof holidayBlockStyles>) => {
  // useEffect(() => {
  //   console.log('TAS HolidayBlock', { holidays, featured_holiday });
  // }, []);

  const global = styled(globalStyles);

  const renderHolidayMeta = (holiday: Holiday) => {
    return (
      <div
        className={`holiday-meta d-flex flex-column flex-grow-1 justify-content-start pb-2 w-100`}
      >
        <div
          style={{
            color: colors.black,
          }}
        >
          <Icon icon="history" className={`mr-3 ${styles.holidayIcon}`} />
          {holiday.days > 0 && `${holiday.days} days, `}
          {holiday.nights} nights
        </div>
        <span
          className={`holiday-web-selling-points check-bullet-list ${styles.holidayInclusions}`}
          dangerouslySetInnerHTML={{ __html: holiday.web_selling_point }}
        />
      </div>
    );
  };

  const renderFeaturedHoliday = (holiday: Holiday) => {
    // console.log('Featured Holiday', {holiday});
    return (
      <React.Fragment key={holiday.id}>
        <a
          className={'d-none d-lg-block holiday-card-clickable'}
          href={getHolidaySlug(holiday)}
        >
          <div
            key={holiday.slug}
            className={`d-flex flex-row holiday-card-featured bg-white ${styles.featuredHoliday}`}
            style={{
              flexWrap: 'nowrap',
              gap: '2rem',
            }}
          >
            <div
              className={`${styles.featuredHolidayImageContainer}`}
              style={{
                flexBasis: `calc(${
                  (100 * (columns_per_row - 1)) / columns_per_row
                }%)`,
                maxWidth: `calc(${
                  (100 * (columns_per_row - 1)) / columns_per_row
                }%)`,
              }}
            >
              <img
                src={`${holiday.feature_images[0].path}?w=800`}
                alt={holiday.feature_images[0].description}
              />
            </div>
            <div
              className={`${styles.featuredHolidayContentContainer} pt-3 pr-4`}
              style={{
                flexBasis: `calc(${100 / columns_per_row}% - 1rem)`,
                maxWidth: `calc(${100 / columns_per_row}% - 1rem)`,
              }}
            >
              <h5 className={`p-0 ${styles.holidayTitle}`}>{holiday.title}</h5>
              <div className={`d-flex flex-column`}>
                {renderHolidayMeta(holiday)}
                <div
                  className={`d-flex flex-row flex-wrap`}
                  style={{ gap: '0.5em' }}
                >
                  <div
                    className={`w-50 d-flex align-items-center ${styles.featuredHolidayPrice}`}
                  >
                    <small
                      style={{
                        color: '#000',
                        fontSize: '1rem',
                        paddingRight: '0.5rem',
                      }}
                    >
                      From
                    </small>
                    {formatHolidayCostAsInteger(
                      holiday.price_cents || holiday.solo_price_cents
                    )}
                    <small
                      className={'pp-abbrev'}
                      style={{ fontWeight: 700, fontSize: font.xs }}
                    >
                      PP
                    </small>
                  </div>
                  <div className={`w-50 d-flex flex-column text-right`}>
                    <div className={`d-flex justify-content-end`}>
                      <Button
                        className={styles.holidayMoreDetailsButton}
                        inverted={true}
                        href={getHolidaySlug(holiday)}
                      >
                        Learn more
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </a>
        <HolidayCard
          {...{
            holiday,
            desktop_spacing,
            mobile_spacing,
            background_colour: '',
            block: {
              title,
              subtitle,
              columns_per_row,
              holidays,
              cta_button,
            },
            featured_holiday: false,
            type,
            classes: 'd-lg-none',
            brand,
          }}
        />
      </React.Fragment>
    );
  };

  return (
    holidays.length > 0 ? (
      <BlockWrapper
        {...{
          type,
          classes,
          brand,
          background_colour,
          desktop_spacing,
          mobile_spacing,
        }}
      >
        <Spacer
          space={['m', 'l']}
          className={cx(`holidays-container container`, styles.container)}
        >
          {(cta_button.label || cta_button.icon) &&
            (cta_button.button_position == ButtonPosition.Above ||
              cta_button.button_position == ButtonPosition.AboveAndBelow) && (
              <CallToActionButton {...cta_button} />
            )}

          <BlockTitle {...{ title, title_anchor: _.kebabCase(`holiday-block-title-${title}`), subtitle, brand }} />

          {holidays.length > 0 && (
            <div
              className={cx(
                `holiday-cards-container mb-3`,
                styles.holidaysCardsContainer
              )}
            >
              {holidays &&
                holidays.map((holiday, index) => {
                  if (featured_holiday && index == 0) {
                    return renderFeaturedHoliday(holiday);
                  }

                  return (
                    <HolidayCard
                      key={index}
                      {...{
                        holiday,
                        desktop_spacing,
                        mobile_spacing,
                        background_colour,
                        block: {
                          title,
                          subtitle,
                          columns_per_row,
                          holidays,
                          cta_button,
                        },
                        featured_holiday,
                        type,
                        classes,
                        brand,
                      }}
                    />
                  );
                })}
            </div>
          )}

          {(cta_button.label || cta_button.icon) &&
            (cta_button.button_position == ButtonPosition.Below ||
              cta_button.button_position == ButtonPosition.AboveAndBelow) && (
              <CallToActionButton {...cta_button} />
            )}
        </Spacer>
      </BlockWrapper>
    ) : <></>
  );
};

export default compose(styled(holidayBlockStyles))(HolidayBlock);
